import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Wybierz...',
  className = '',
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <div
      ref={ref}
      className={`custom-select ${isOpen ? 'open' : ''} ${size === 'sm' ? 'custom-select--sm' : ''} ${className}`}
    >
      <button
        type="button"
        className="custom-select__trigger"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span className={`custom-select__label ${!selectedOption ? 'custom-select__label--placeholder' : ''}`}>
          {displayLabel}
        </span>
        <FiChevronDown className={`custom-select__chevron ${isOpen ? 'custom-select__chevron--open' : ''}`} size={14} />
      </button>

      {isOpen && (
        <div className="custom-select__dropdown">
          <div className="custom-select__options">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`custom-select__option ${option.value === value ? 'custom-select__option--selected' : ''}`}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
                {option.value === value && <FiCheck size={14} className="custom-select__check" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select;
